import {
  COMMON_VALIDATIONS,
  INPUT_LENGTH_MIN_MAX_VALUES,
  NON_HYPHENED_UUID_LENGTH,
} from '@/constant/global/global.constant';
import _ from 'lodash';

export const getEllipsis = (
  displayValue: string,
  max: number,
  nameKey?: string,
) => {
  if (nameKey === 'marketsDropdownDisplayValue') {
    return displayValue?.slice(0, 30);
  }
  return displayValue?.length >= max
    ? `${displayValue?.slice(0, max)} ...`
    : displayValue;
};

/**
 * @description Downloading files
 */
export const downloadFile = (blob: any, fileName: string, fileType: string) => {
  const url = window.URL.createObjectURL(new Blob([blob], { type: fileType }));
  const anchorElement = document.createElement('a');
  anchorElement.href = url;
  anchorElement.setAttribute('download', `${fileName}`);
  anchorElement.target = '_blank';
  document.body.appendChild(anchorElement);
  anchorElement.click();
  anchorElement?.parentNode?.removeChild(anchorElement);
  return anchorElement;
};

export function firstLetterCapitalise(givenString: string | unknown): string {
  if (typeof givenString !== 'string') {
    return '';
  }
  return givenString
    ?.toLowerCase()
    ?.split(' ')
    ?.map((word) => _.upperFirst(word))
    ?.join(' ');
}

export const formatPrice = (
  value: number | null | undefined,
  roundOffDigits: number = 2,
): string => {
  if (value === null || value === undefined) {
    return '0.00';
  }
  return value.toFixed(roundOffDigits);
};

// Input Validations

export const emptySpaceValidations = (
  value: any,
  options?: {
    allowWhiteSpacesInBetween?: boolean;
  },
) => {
  const trimmedValue = value?.trim() || '';
  if (/\s{2,}/.test(value)) return 'Consecutive spaces are not allowed.';
  if (value?.startsWith(' ')) return 'Cannot enter space at the beginning.';
  if (/\s{2,}/.test(trimmedValue)) return 'Consecutive spaces are not allowed.';
  if (value?.endsWith(' ')) return 'Cannot enter space at the end.';
  if (!options?.allowWhiteSpacesInBetween && /\s+/.test(value)) {
    return 'Cannot enter empty spaces.';
  }
};

export const validationFunction = (
  value: any,
  options?: {
    allowWhiteSpacesInBetween?: boolean;
  },
) => {
  const trimmedValue = value?.trim() || '';
  const validationResult = emptySpaceValidations(value, options);
  if (validationResult) return validationResult;
  if (!trimmedValue) return 'Input cannot be empty or only spaces.';
  if (
    trimmedValue.length < INPUT_LENGTH_MIN_MAX_VALUES.MIN_LENGTH_VALUE ||
    trimmedValue.length > INPUT_LENGTH_MIN_MAX_VALUES.MAX_LENGTH_VALUE
  ) {
    return COMMON_VALIDATIONS.MIN_MAX_VALIDATION_MSG;
  }
  return true;
};

export const getCustomisedValidationRules = (options?: {
  allowWhiteSpacesInBetween?: boolean;
}) => ({
  validate: (value: any) => validationFunction(value, options),
  required: {
    value: true,
    message: COMMON_VALIDATIONS.REQUIRED_VALIDATION_MSG,
  },
});

export function customUUIDv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Converts 32-char UUID ↔ 36-char UUID (with hyphens)
export const toggleHyphenation = (
  uuid: string,
  toHyphenated: boolean,
): string => {
  if (!uuid) return uuid;

  // Remove all hyphens
  const raw = uuid.replace(/-/g, '');

  if (!toHyphenated) {
    // Return without hyphens
    return raw;
  }

  // Add hyphens back (8-4-4-4-12 format)
  if (raw.length === NON_HYPHENED_UUID_LENGTH) {
    return raw.replace(
      /([a-fA-F0-9]{8})([a-fA-F0-9]{4})([a-fA-F0-9]{4})([a-fA-F0-9]{4})([a-fA-F0-9]{12})/,
      '$1-$2-$3-$4-$5',
    );
  }

  return uuid; // fallback if length doesn't match
};

export const isValidString = (val: any) =>
  typeof val === 'string' && val?.trim() !== '';
